// Copyright 2025, University of Maryland, All Rights Reserved
import { useCallback } from 'react'
import { useLLMConfigs } from '@/subsystems/workspaces/services'
import type { AnalysisPromptConfig } from '../types'
import type { ConfigUpdater } from './useAnalysisPromptState'
import { toStringOrNull } from '../utils'

interface UseAnalysisModelStateParams {
  analysisConfig: AnalysisPromptConfig
  updateConfig: ConfigUpdater
}

export const useAnalysisModelState = ({ analysisConfig, updateConfig }: UseAnalysisModelStateParams) => {
  const modelVariant = toStringOrNull(analysisConfig.modelVariant)

  // Fetch LLM configs to get model capabilities
  const { data: llmConfigs = [] } = useLLMConfigs({ activeOnly: true })

  const currentModelConfig = llmConfigs.find(config => config.modelName === modelVariant)

  const supportsCaching = currentModelConfig?.cachingSupported ?? false

  const handleModelChange = useCallback(
    (value: string) => updateConfig({ modelVariant: value }),
    [updateConfig],
  )

  // Long context fallback state
  const longContextFallbackEnabled = analysisConfig.longContextFallbackEnabled ?? false
  const longContextFallbackModel = analysisConfig.longContextFallbackModel ?? null

  const handleLongContextFallbackEnabledChange = useCallback(
    (value: boolean) => updateConfig({ longContextFallbackEnabled: value }),
    [updateConfig],
  )

  const handleLongContextFallbackModelChange = useCallback(
    (value: string) => updateConfig({ longContextFallbackModel: value }),
    [updateConfig],
  )

  return {
    modelVariant,
    supportsCaching,
    longContextFallbackEnabled,
    longContextFallbackModel,
    handlers: {
      handleModelChange,
      handleLongContextFallbackEnabledChange,
      handleLongContextFallbackModelChange,
    },
  }
}
